import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {TradeTag} from '@cp/model';
import {Delete, Edit, Save, Add, ExpandMore} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import {listTradeTags} from '../../../api/tradeTag/listTradeTags';
import {createTradeTag} from '../../../api/tradeTag/createTradeTag';
import {updateTradeTag} from '../../../api/tradeTag/updateTradeTag';
import {deleteTradeTag} from '../../../api/tradeTag/deleteTradeTag';

interface EditableTradeTag extends TradeTag {
  isEditing?: boolean;
}

export default function AdminTradeTagPage() {
  const {showMessage} = useMessage();
  const [tags, setTags] = useState<EditableTradeTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const loadTags = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTradeTags();
      setTags(data);
    } catch (error) {
      console.error('Failed to load trade tags', error);
      showMessage('Failed to load trade tags', 6000, 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    void loadTags();
  }, [loadTags]);

  const handleCreate = async () => {
    if (!newLabel.trim()) {
      showMessage('Label is required', 4000, 'error');
      return;
    }
    setSavingId('new');
    try {
      const created = await createTradeTag({label: newLabel.trim(), description: newDescription.trim() || undefined});
      setTags((prev) => [...prev, created]);
      setNewLabel('');
      setNewDescription('');
      showMessage('Trade tag created', 4000, 'success');
    } catch (error) {
      console.error('Failed to create trade tag', error);
      showMessage('Failed to create trade tag', 6000, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleEditToggle = (id: string, editing: boolean) => {
    setTags((prev) => prev.map((tag) => (tag.id === id ? {...tag, isEditing: editing} : tag)));
  };

  const handleFieldChange = (id: string, field: 'label' | 'description', value: string) => {
    setTags((prev) => prev.map((tag) => (tag.id === id ? {...tag, [field]: value} : tag)));
  };

  const handleSave = async (tag: EditableTradeTag) => {
    if (!tag.label.trim()) {
      showMessage('Label is required', 4000, 'error');
      return;
    }
    setSavingId(tag.id);
    try {
      await updateTradeTag(tag.id, {label: tag.label.trim(), description: tag.description?.trim() || undefined});
      setTags((prev) => prev.map((t) => (t.id === tag.id ? {...tag, isEditing: false} : t)));
      showMessage('Trade tag updated', 4000, 'success');
    } catch (error) {
      console.error('Failed to update trade tag', error);
      showMessage('Failed to update trade tag', 6000, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setSavingId(id);
    try {
      await deleteTradeTag(id);
      setTags((prev) => prev.filter((tag) => tag.id !== id));
      showMessage('Trade tag deleted', 4000, 'success');
    } catch (error) {
      console.error('Failed to delete trade tag', error);
      showMessage('Failed to delete trade tag', 6000, 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <ContentContainer data-testid="admin-trade-tag-page">
      <Typography variant="h5" component="h2" sx={{mb: 2}}>
        Trade Tags
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
        Manage reusable tags (label and optional description) for trading journal entries.
      </Typography>

      <Accordion sx={{mb: 3}} defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1">Add new trade tag</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
            <TextField
              label="Label"
              size="small"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              sx={{minWidth: {xs: '100%', sm: 200}}}
            />
            <TextField
              label="Description"
              size="small"
              multiline
              minRows={2}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              sx={{flex: 1, minWidth: {xs: '100%', sm: 240}}}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              disabled={savingId === 'new' || loading}
              sx={{textTransform: 'none', alignSelf: {xs: 'stretch', sm: 'center'}}}
            >
              Add tag
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Box sx={{overflowX: 'auto'}}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.map((tag) => (
              <TableRow key={tag.id}>
                <TableCell sx={{minWidth: 160}}>
                  {tag.isEditing ? (
                    <TextField
                      size="small"
                      value={tag.label}
                      onChange={(e) => handleFieldChange(tag.id, 'label', e.target.value)}
                    />
                  ) : (
                    tag.label
                  )}
                </TableCell>
                <TableCell sx={{minWidth: 240}}>
                  {tag.isEditing ? (
                    <TextField
                      size="small"
                      multiline
                      minRows={2}
                      value={tag.description ?? ''}
                      onChange={(e) => handleFieldChange(tag.id, 'description', e.target.value)}
                    />
                  ) : (
                    tag.description
                  )}
                </TableCell>
                <TableCell align="right">
                  {tag.isEditing ? (
                    <IconButton
                      aria-label="Save"
                      onClick={() => handleSave(tag)}
                      disabled={savingId === tag.id}
                      size="small"
                    >
                      <Save fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      aria-label="Edit"
                      onClick={() => handleEditToggle(tag.id, true)}
                      size="small"
                      disabled={savingId != null}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    aria-label="Delete"
                    onClick={() => setDeleteId(tag.id)}
                    size="small"
                    disabled={savingId === tag.id}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {tags.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No trade tags yet. Create your first tag above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <ConfirmationDialog
        title="Delete trade tag"
        content="Are you sure you want to delete this trade tag?"
        confirmButtonLabel="Delete"
        confirmButtonColor="error"
        open={deleteId != null}
        loading={savingId === deleteId}
        handleConfirm={() => {
          if (!deleteId) return;
          void handleDelete(deleteId);
          setDeleteId(null);
        }}
        handleCancel={() => setDeleteId(null)}
        onClose={() => setDeleteId(null)}
      />
    </ContentContainer>
  );
}
